import type {
  ControlSummary,
  Finding,
  Framework,
  ProductSummary,
  RiskAcceptance,
  Scan,
} from '../api.ts';
import { Findings } from '../Findings.tsx';
import { asDate, asMoment } from './format.ts';

interface FindingsPanelProps {
  latest: Scan | null;
  findings: Finding[] | null;
  controls: ControlSummary[];
  products: ProductSummary[];
  /** The frameworks this client's report covers. */
  frameworks: Framework[];
  acceptances: RiskAcceptance[];
}

/**
 * The findings of the latest assessment, rendered by the same component the
 * demo and the shared report use. It takes its data as props and fetches
 * nothing, which is the only reason one component can serve all three.
 */
export const FindingsPanel = ({
  latest,
  findings,
  controls,
  products,
  frameworks,
  acceptances,
}: FindingsPanelProps) => {
  if (latest === null) {
    return (
      <div className="empty">
        <span className="empty-title">Nothing has been assessed yet</span>
        <p className="empty-body">
          Findings appear here once this client’s first assessment completes. Start one from the
          Overview tab.
        </p>
      </div>
    );
  }

  if (findings === null) {
    return (
      <p className="note" aria-live="polite">
        Reading the findings of the assessment completed on{' '}
        {latest.completedAt ? asDate(latest.completedAt) : 'this client'}…
      </p>
    );
  }

  return (
    <>
      {latest.completedAt && (
        <p className="note td-stamp">
          The assessment completed on {asMoment(latest.completedAt)}. These findings are a record of
          that moment, not a live view of the tenant.
        </p>
      )}
      <Findings
        findings={findings}
        controls={controls}
        products={products}
        frameworks={frameworks}
        acceptances={acceptances}
      />
    </>
  );
};
